import { useSelector } from '@xstate/store/react';
import { FC } from 'react';

import {
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Stack,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { AudioDeviceOption } from '@eluve/mic-utils';
import { waveformStore } from '@eluve/stores';

import { InlineMicrophoneLevelBars } from '../InlineMicrophoneLevelBars';

import { useMicrophoneDeviceSelector } from './useMicrophoneDeviceSelector';

export interface MicrophoneDeviceSelectorProps {
  getDevices?: () => Promise<AudioDeviceOption[]>;
  /** Optional side-effect hook (e.g. toasts) — persistence happens inside
   * useMicrophoneDeviceSelector. */
  onSelectedDeviceChange?: (device: AudioDeviceOption | null) => void;
  selectedId: string | null;
  usesPortal?: boolean;
  preferredMicrophoneNames?: string[];
  side?: 'top' | 'bottom' | 'left' | 'right';
  hasError?: boolean;
  disabled?: boolean;
  showIcon?: boolean;
  showBars?: boolean;
}

export const MicrophoneDeviceSelector: FC<MicrophoneDeviceSelectorProps> = ({
  getDevices,
  onSelectedDeviceChange,
  selectedId,
  usesPortal,
  preferredMicrophoneNames,
  side,
  hasError,
  disabled,
  showIcon,
  showBars,
}) => {
  const { t } = useTranslation();
  const waveformData = useSelector(
    waveformStore,
    (s) => s.context.waveformData,
  );

  const { options, selectedMicrophone, handleValueChange, handleOpenChange } =
    useMicrophoneDeviceSelector({
      getDevices,
      onSelectedDeviceChange,
      selectedId,
      preferredMicrophoneNames,
    });

  return (
    <Select
      disabled={disabled ?? options.length === 0}
      onValueChange={handleValueChange}
      onOpenChange={handleOpenChange}
      value={selectedId ?? ''}
      hasError={hasError}
    >
      <SelectTrigger
        aria-label={t('smartBlocks.microphoneSelector.ariaLabel')}
        className={showIcon || showBars ? 'bg-bg-secondary' : ''}
      >
        {showIcon || showBars ? (
          <Stack
            className="min-w-0 flex-1"
            direction="horizontal"
            align="center"
            gap={2}
            fullWidth={false}
          >
            {showIcon && (
              <Icon name="Mic" className="shrink-0 text-text-secondary" />
            )}
            <span className="min-w-0 flex-1 truncate text-left">
              {selectedMicrophone?.label ?? (
                <span className="text-text-supporting">
                  {t('smartBlocks.microphoneSelector.selectMicrophone')}
                </span>
              )}
            </span>
            {showBars && (
              <InlineMicrophoneLevelBars
                waveformData={waveformData}
                className="shrink-0"
              />
            )}
          </Stack>
        ) : (
          (selectedMicrophone?.label ?? (
            <span className="text-text-supporting">
              {t('smartBlocks.microphoneSelector.selectMicrophone')}
            </span>
          ))
        )}
      </SelectTrigger>
      <SelectContent usesPortal={usesPortal} side={side}>
        {options.map((device, index) => (
          <SelectItem
            key={`${device.deviceId}-${index}`}
            className="pl-7 focus:bg-bg-accent-quaternary"
            value={device.deviceId}
          >
            {device.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
